import React, { useState, useEffect } from "react";
import { Chart } from "react-google-charts";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { getLocationAnalytics } from "../services/dashboardService";

const LocationAnalytics = () => {
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [countries, setCountries] = useState(["Worldwide"]);
  const [selectedCountry, setSelectedCountry] = useState("Worldwide");
  const [selectedTimeframe, setSelectedTimeframe] = useState("1M");
  const [tableHeader, setTableHeader] = useState("Country");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    const isWorldwide = selectedCountry === "Worldwide";

    getLocationAnalytics(selectedTimeframe, selectedCountry)
      .then((apiData) => {
        if (!apiData?.success) {
          throw new Error(apiData?.message || apiData?.error || "Failed to fetch location data");
        }

        if (isWorldwide) {
          const uniqueCountries = [
            "Worldwide",
            ...new Set((apiData.data || []).map((item) => item.country)),
          ];
          setCountries(uniqueCountries);
        }

        const dataKey = isWorldwide ? "country" : "region";
        const tableAggregated = (apiData.data || []).reduce((acc, item) => {
          const key = item[dataKey];
          if (key && key !== "(not set)") {
            if (!acc[key]) acc[key] = 0;
            acc[key] += item.users;
          }
          return acc;
        }, {});
        const newTableData = Object.entries(tableAggregated).sort((a, b) => b[1] - a[1]);
        setTableData(newTableData);
        setTableHeader(isWorldwide ? "Country" : "Region");

        if (isWorldwide) {
          setChartData([["Country", "Users"], ...newTableData]);
        }
      })
      .catch((err) => {
        console.warn("Location analytics unavailable:", err);
        setChartData([]);
        setTableData([]);
        const message =
          err?.response?.status === 401
            ? "Session expired. Please sign in again to the business dashboard."
            : err?.response?.data?.error ||
              err?.response?.data?.message ||
              err?.message ||
              "Failed to load location analytics";
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [selectedCountry, selectedTimeframe]);

  const handleCountryChange = (event) => setSelectedCountry(event.target.value);
  const handleTimeframeChange = (event, newTimeframe) => {
    if (newTimeframe !== null) {
      setSelectedTimeframe(newTimeframe);
    }
  };

  const chart = (
    <Chart
      chartType="GeoChart"
      width="100%"
      height={window.innerWidth < 640 ? "280px" : "400px"}
      data={chartData}
      options={{
        colorAxis: { colors: ["#a7d8a7", "#4f964f", "#005f00"] },
        backgroundColor: "#f5f5f5",
        datalessRegionColor: "#d3d3d3",
      }}
    />
  );

  const table = (
    <TableContainer component={Paper} sx={{ mt: 4 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{tableHeader}</TableCell>
            <TableCell align="right">Active Users</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map(([name, users]) => (
            <TableRow key={name}>
              <TableCell>{name}</TableCell>
              <TableCell align="right">{users}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          User Location Analytics
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <ToggleButtonGroup
            value={selectedTimeframe}
            exclusive
            onChange={handleTimeframeChange}
            size="small"
          >
            <ToggleButton value="1D">1D</ToggleButton>
            <ToggleButton value="1W">1W</ToggleButton>
            <ToggleButton value="1M">1M</ToggleButton>
            <ToggleButton value="6M">6M</ToggleButton>
            <ToggleButton value="ALL">ALL</ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Country</InputLabel>
            <Select value={selectedCountry} label="Country" onChange={handleCountryChange}>
              {countries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : (
          <>
            {chartData.length > 1 ? (
              chart
            ) : (
              <Typography variant="body2" color="text.secondary">
                No location data available for this period.
              </Typography>
            )}
            {tableData.length > 0 && table}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LocationAnalytics;
